import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAIIDELabel, getAllowedAIIDEs } from './homeworkUtils'
import { isChatlogMarkdownLike, submitChatlogFiles } from '../../utils/submitChatlogFiles'

export default function ChatLogSubmitPanel({
  homework,
  draft = {},
  onDraftChange,
  getStudentId,
  buildIdentityHeaders,
  onSubmitted,
}) {
  const { t } = useTranslation()
  const allowed = getAllowedAIIDEs(homework)
  const ide = draft.ide && allowed.includes(draft.ide) ? draft.ide : (allowed[0] || 'trae')
  const message = draft.message || ''
  const files = draft.files || []
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const setFiles = async (fileList) => {
    setError('')
    const next = []
    for (const file of fileList) {
      if (!file.name.toLowerCase().endsWith('.md')) {
        setError(`仅支持 .md 文件：${file.name}`)
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`文件大小不能超过 10MB：${file.name}`)
        return
      }
      const bad = await isChatlogMarkdownLike(file)
      if (bad) {
        setError(`${file.name} 可能不是对话日志：${bad}`)
        return
      }
      next.push(file)
    }
    onDraftChange?.({ ...draft, files: next })
  }

  const handleSubmit = async () => {
    if (!files.length) {
      setError('请先选择 ChatLogs 文件')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const studentId = await getStudentId?.()
      const taskId = homework?.id || homework?.TrainingAssignmentID
      const headers = (await buildIdentityHeaders?.()) || {}
      await submitChatlogFiles({
        trainingTaskId: taskId,
        studentId,
        ide,
        files,
        submitMessage: message || '',
        headers,
      })
      onDraftChange?.({ ...draft, files: [], message: '' })
      onSubmitted?.()
    } catch (err) {
      setError(err.message || '提交失败')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--hwb-soft)' }}>
        当前允许的来源：{allowed.map(getAIIDELabel).join('、')}。请上传对应平台导出的 Markdown（YouLaber 请用面板「导出对话」得到的 chatlog.md，可自选最好的一份）。
      </p>
      <div className="hwb-submit-tools">
        <select
          className="hwb-select"
          value={ide}
          onChange={(e) => onDraftChange?.({ ...draft, ide: e.target.value })}
        >
          {allowed.map((item) => (
            <option key={item} value={item}>{getAIIDELabel(item)}</option>
          ))}
        </select>
        <label className="hwb-btn hwb-btn-secondary" style={{ display: 'inline-flex', alignItems: 'center' }}>
          选择 .md 文件
          <input
            hidden
            multiple
            type="file"
            accept=".md,text/markdown"
            onChange={(e) => {
              setFiles(Array.from(e.target.files || []))
              e.target.value = ''
            }}
          />
        </label>
      </div>
      {files.length > 0 ? (
        <p style={{ fontSize: 12, color: 'var(--hwb-soft)', margin: '0 0 8px' }}>
          已选 {files.length} 个文件：{files.map((f) => f.name).join('、')}
        </p>
      ) : null}
      <textarea
        className="hwb-textarea"
        rows={2}
        value={message}
        onChange={(e) => onDraftChange?.({ ...draft, message: e.target.value })}
        placeholder={t('hw_message_placeholder')}
      />
      {error ? <p style={{ color: 'var(--hwb-danger)', fontSize: 12, margin: '8px 0 0' }}>{error}</p> : null}
      <div className="hwb-submit-bar">
        <div className="hwb-submit-selected">上传 ChatLogs 作为作业提交</div>
        <button
          type="button"
          className="hwb-btn hwb-btn-primary"
          disabled={!files.length || submitting}
          onClick={handleSubmit}
        >
          {submitting ? '提交中…' : '提交 ChatLogs'}
        </button>
      </div>
    </div>
  )
}
